import type {CSSProperties} from 'react';
import {wedgeExactRole} from './wedgeRoles.js';

export type WedgeSideNavLook =
  | 'sidenav-brand' | 'sidenav-brand-collapsed'
  | 'sidenav-item' | 'sidenav-item-active' | 'sidenav-item-collapsed' | 'sidenav-item-collapsed-active'
  | 'sidenav-edge-toggle';

const SIDENAV_LOOKS = new Set<string>([
  'sidenav-brand', 'sidenav-brand-collapsed',
  'sidenav-item', 'sidenav-item-active', 'sidenav-item-collapsed', 'sidenav-item-collapsed-active',
  'sidenav-edge-toggle',
]);
export const isWedgeSideNavLook = (look: string): look is WedgeSideNavLook => SIDENAV_LOOKS.has(look);

/* Released controls of the desktop side rail (the primary nav). None draws a transparent target: the rows are 40px or more tall
   and span the rail, the brand block is a wide block, and the edge toggle is a 24px disc whose class sheet owns its place on the
   rail edge.
   The brand block is a transparent column (expanded: 14px by 0 by 26px padding, centred) or a centred row (collapsed: 10px by 0 by
   20px padding). It shows the default cursor while it has no handler (disabled).
   A rail row is a full width flex row with a 15px type, a 11px radius, no border and a relative anchor for the active bar and the
   collapsed badge. Expanded it has a 13px gap and 11px by 12px padding; collapsed it has no gap, 12px by 8px padding and centres.
   Inactive, it sets no fill (so the shipped hover sheet paints it), the secondary label ink and weight 500. Active, it sets weight 650
   and no fill or ink: the caller passes the tint and ink of the tab from its theme palette (one pair per tab and theme), as the
   legacy rail did inline. The padding and the gap ease like the shipped row.
   The edge toggle declares nothing: its disc, edge and fade are the shipped .wedge-rail-edge-toggle sheet. */
export function wedgeSideNavStyle(look: WedgeSideNavLook, disabled = false): CSSProperties {
  const r = wedgeExactRole;
  const cursor = disabled ? 'default' : 'pointer';
  switch (look) {
    case 'sidenav-brand': return {
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      padding: '14px 0 26px', background: 'transparent', border: 0, cursor,
    };
    case 'sidenav-brand-collapsed': return {
      display: 'flex', justifyContent: 'center', padding: '10px 0 20px',
      background: 'transparent', border: 0, cursor,
    };
    case 'sidenav-edge-toggle': return {};
    default: {
      const collapsed = look === 'sidenav-item-collapsed' || look === 'sidenav-item-collapsed-active';
      const on = look === 'sidenav-item-active' || look === 'sidenav-item-collapsed-active';
      return {
        display: 'flex', alignItems: 'center', gap: collapsed ? 0 : 13,
        justifyContent: collapsed ? 'center' : 'flex-start',
        width: '100%', textAlign: 'left',
        padding: collapsed ? '12px 8px' : '11px 12px', borderRadius: 11,
        ...(on ? {} : {color: r('labelSec')}),
        border: 0, cursor: 'pointer',
        fontFamily: r('font'), fontSize: 15,
        fontWeight: on ? 650 : 500,
        position: 'relative',
        transition: `padding 160ms ${r('ease')}, gap 160ms ${r('ease')}`,
      };
    }
  }
}
